<template>
    <div class="comanda-detalii-container">
        <div v-if="isLoading" class="mb-2 dx-fieldset loading-state">
            <div class="dx-fieldset-header">Se încarcă Comanda...</div>
        </div>

        <div v-else-if="comanda" class="mb-2 dx-fieldset header-fields">
            <div class="dx-fieldset-header">Detalii Comandă ({{ comanda.Cod }})</div>
            
            <div class="header-row">
                
                <div class="dx-field compact-field">
                    <div class="dx-field-label">ID Comandă:</div>
                    <div class="dx-field-value">
                        <DxTextBox :value="comanda.Id" :read-only="true" styling-mode="filled" />
                    </div>
                </div>

                <div class="dx-field compact-field">
                    <div class="dx-field-label">Client:</div>
                    <div class="dx-field-value">
                        <DxTextBox :value="comanda.NumeClient" :read-only="true" styling-mode="filled" />
                    </div>
                </div>

                <div class="dx-field compact-field">
                    <div class="dx-field-label">Data:</div>
                    <div class="dx-field-value">
                        <DxDateBox :value="comanda.DataComenzii" :read-only="true" display-format="dd.MM.yyyy" styling-mode="filled" />
                    </div>
                </div>

                <div class="dx-field compact-field">
                    <div class="dx-field-label">Valoare Totală:</div>
                    <div class="dx-field-value">
                        <DxNumberBox :value="comanda.ValoareTotala" :read-only="true" format="#,##0.00" styling-mode="filled" />
                    </div>
                </div>
            </div>
        </div>
        
        <div v-else-if="props.comandaId" class="mb-2 dx-fieldset error-state">
             <div class="dx-fieldset-header text-red-700">Comanda nu a putut fi găsită.</div>
        </div>

        <DxDataGrid
            :data-source="detaliiStore"
            key-expr="ID"
            show-borders
            :column-auto-width="true"
            :allow-column-reordering="true"
            :scrolling="{ mode: 'virtual' }"
            :filter-row="{ visible: true }"
            :paging="{ pageSize: 20 }"
            height="auto" 
            class="detalii-grid"
        >
            <DxColumn data-field="ProdusCodCPV" caption="Cod CPV" width="120"/>
            <DxColumn data-field="ProdusNume" caption="Produs" width="200"/>
            <DxColumn data-field="Cantitate" caption="Cantitate" data-type="number" width="100"/>
            <DxColumn data-field="PretNegociat" caption="Preț negociat" data-type="number" format="#0.00" width="120"/>
            <DxColumn data-field="Valoare" caption="Valoare" data-type="number" format="#0.00" width="120"/>
            <DxColumn data-field="TermenLivrare" caption="Termen Livrare" data-type="date" width="120"/>
        </DxDataGrid>
    </div>
</template>

<script lang="ts" setup>
import { ref, watch } from 'vue';
import DxDataGrid, { DxColumn } from 'devextreme-vue/data-grid';
import DxTextBox from 'devextreme-vue/text-box';
import DxDateBox from 'devextreme-vue/date-box';
import DxNumberBox from 'devextreme-vue/number-box';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import AppSetting from '@/settings/appSettings';
import axios from 'axios';

// Interfață pentru datele de antet ale comenzii
interface OrderHeaderData {
    Id: number | string;
    Cod: string;
    NumeClient: string; // Presupunem numele câmpului returnat de API
    DataComenzii: string; 
    ValoareTotala: number;
    [key: string]: any; 
}

interface Props { comandaId: string }
const props = defineProps<Props>();

const detaliiStore = ref<any>(null);
const comanda = ref<OrderHeaderData | null>(null);
const isLoading = ref(false); 


watch(() => props.comandaId, async (id) => {
    if(!id) return;

    isLoading.value = true;
    comanda.value = null; 
    
    const token = localStorage.getItem("ERA_Jwt");
    if (!token) {
        console.error("Token JWT lipsă.");
        isLoading.value = false;
        return;
    }

    // 1. Apel Axios pentru antet
    try {
        const urlHeader = `${AppSetting.API_Url_ComercialOdata}PRO_Comenzi(${id})`;
        const response = await axios.get<OrderHeaderData>(urlHeader, {
            headers: { "Authorization": "Bearer " + token }
        });
        comanda.value = response.data; 
    } catch (err) {
        console.error("Eroare la încărcarea antetului comenzii:", err);
    } 

    // 2. Setarea store-ului DevExtreme
    const urlDetalii = `${AppSetting.API_Url_ComercialOdata}PRO_ComenziDetalii?ComandaID=${id}`;

    detaliiStore.value = new DataSource({
        store: new ODataStore({
            url: urlDetalii,
            key:'ID',
            version:4,
            beforeSend: (e:any)=>{
                e.headers = {
                    "Content-Type":"application/json",
                    "Authorization":"Bearer "+token
                };
            }
        })
    });

    // Soluția pentru eroarea .finally()
    detaliiStore.value.load().always(() => { 
        isLoading.value = false;
    });

}, { immediate: true });
</script>

<style scoped>
/* ---------------------------------------------------------------------- */
/* LAYOUT FLEXBOX: Grila să ocupe înălțimea rămasă și lățimea 100% */
/* ---------------------------------------------------------------------- */

/* Containerul principal trebuie să fie un flex container vertical */
.comanda-detalii-container {
    height: 100%; 
    display: flex;
    flex-direction: column;
    /* FOARTE IMPORTANT: Elimină padding-ul lateral pentru a permite grilei să se extindă */
    padding: 10px 0; /* Păstrează padding sus/jos, elimină stânga/dreapta */
    width: 100%; 
}

/* Grila ocupă spațiul rămas */
.detalii-grid {
    flex-grow: 1; 
    min-height: 200px; 
    height: 0; 
    /* Adaugă lățimea 100% explicit (deși flex-grow 1 ar trebui să fie suficient) */
    width: 100%;
}

/* ---------------------------------------------------------------------- */
/* STILURI PENTRU ANTET COMPACT (Câmpuri Mici) */
/* ---------------------------------------------------------------------- */

.header-fields {
    flex-shrink: 0; 
    padding: 5px; 
    /* Adaugă padding lateral câmpurilor, care era pe containerul principal */
    padding: 5px 10px; /* Padding-ul lateral de 10px menține alinierea cu grila */
}

/* Structura FieldSet: asigură că cele 4 câmpuri stau pe un rând */
.header-row {
    display: flex;
    flex-wrap: wrap; 
    gap: 15px; 
}

/* Stil pentru fiecare câmp individual (restul rămâne la fel) */
.compact-field {
    flex: 1 1 200px; 
    margin-bottom: 5px; 
}

/* Vizează eticheta și valoarea DevExtreme pentru a le face mai compacte */
.compact-field >>> .dx-field-label {
    padding-top: 5px; 
    font-size: 0.85rem; 
    width: 100px; 
}

.compact-field >>> .dx-texteditor-input {
    padding: 6px 10px; 
    min-height: unset;
}
</style>